import { useState, useEffect, useMemo, useCallback } from 'react';
import { API_URL, authHeaders } from '../../../utils/api';
import { useAuth } from '../../../contexts/AuthContext';
import useApiCache from '../../../hooks/useApiCache';

/**
 * Äger all logik bakom leverantörserbjudandena: hämtning, favoriter,
 * kontaktförfrågningar och de tre kontaktvägarna (direct / leadme / affiliate).
 *
 * Delas av kundens Erbjudanden-sida och sektionen i MyProjects, så att
 * kontaktflödet bara finns på ett ställe.
 */

const hamtaLeverantorer = async () => {
  const response = await fetch(`${API_URL}/suppliers`, { headers: authHeaders() });
  if (!response.ok) throw new Error('Kunde inte hämta leverantörer');
  return response.json();
};

const hamtaFavoriter = async () => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${API_URL}/suppliers/favorites`, {
    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
  });
  if (!response.ok) throw new Error('Kunde inte hämta favoriter');
  const favoriter = await response.json();
  // Array, inte Set: useApiCache lagrar i sessionStorage via JSON.stringify,
  // och en Set serialiseras till {} — favoriterna hade försvunnit vid omladdning.
  return favoriter.map(f => f._id);
};

const hamtaAnvandarId = () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return 'anon';
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.id || 'anon';
  } catch {
    return 'anon';
  }
};

// Leverantörer vi vill lyfta fram, i den ordning de ska visas
const PRIORITERAD_ORDNING = ['Kakelspecialisten', 'Ballingslöv', 'Decoform', 'Granitop', 'Kvadratmeter'];

const useErbjudanden = ({ projects = [], procurement = null, activeProject = null } = {}) => {
  const { user } = useAuth();
  const userId = hamtaAnvandarId();

  // Samma cache-nycklar som MyProjects använde, så de två ytorna delar cache
  const { data: suppliers, loading: suppliersLoading } = useApiCache(
    `myprojects_suppliers_${userId}`,
    hamtaLeverantorer,
    600000,
    [],
    []
  );

  const { data: savedOffers, refresh: refreshFavorites } = useApiCache(
    `myprojects_favorites_${userId}`,
    hamtaFavoriter,
    300000,
    [],
    []
  );

  // Håller hjärtat responsivt medan anropet är i luften
  const [optimistiskaFavoriter, setOptimistiskaFavoriter] = useState(null);

  const savedOffersSet = useMemo(() => {
    if (optimistiskaFavoriter) return optimistiskaFavoriter;
    if (savedOffers instanceof Set) return savedOffers;
    if (Array.isArray(savedOffers)) return new Set(savedOffers);
    return new Set();
  }, [savedOffers, optimistiskaFavoriter]);

  const [contactRequests, setContactRequests] = useState({});
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [showSupplierEmailModal, setShowSupplierEmailModal] = useState(false);
  const [showLeadmeContactModal, setShowLeadmeContactModal] = useState(false);
  const [contactResponsible, setContactResponsible] = useState(null);
  const [showInfoModal, setShowInfoModal] = useState(false);

  const fetchContactRequests = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/supplier-contact-requests/my-requests`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const map = {};
        data.forEach(r => { map[r.supplier] = r.requestedAt; });
        setContactRequests(map);
      }
    } catch {}
  }, []);

  useEffect(() => {
    if (user?.role === 'kund') fetchContactRequests();
  }, [user, fetchContactRequests]);

  // Skickar notis till bokning@ när kunden klickar sig vidare via affiliate-länk
  const sendAffiliateClickNotification = useCallback(async (supplier) => {
    try {
      const token = localStorage.getItem('token');

      const messageBody = `
Hej Leadme!

En kund har klickat på en affiliate-länk till leverantören ${supplier.companyName}.

Kundinfo:
- Namn: ${user ? `${user.firstName} ${user.lastName}` : 'Okänd kund'}
- E-post: ${user?.email || 'Ej tillgänglig'}
- Tidpunkt: ${new Date().toLocaleString('sv-SE')}

Leverantörsinformation:
- Företagsnamn: ${supplier.companyName}
- Typ: ${supplier.supplierType}
- Kontaktperson: ${supplier.contactPerson || supplier.email || 'Ej angivet'}
- E-post: ${supplier.email}
- Telefon: ${supplier.phone}
- Affiliate-URL: ${supplier.affiliateUrl}

Detta meddelande är automatiskt genererat från Leadme Kundportal.
`;

      await fetch(`${API_URL}/api/mail/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to: 'bokning@leadme.se',
          subject: `Affiliate-klick: ${supplier.companyName}`,
          message: messageBody,
          senderName: 'Leadme Kundportal',
          recipientName: 'Leadme Bokning'
        }),
      });
    } catch (error) {
      // Tysta fel - kunden ska komma vidare till leverantören ändå
      console.error('Error sending affiliate click notification:', error);
    }
  }, [user]);

  const handleContactRequest = useCallback((supplier) => {
    // Förfrågan ska gå till IA:n på det projekt kunden faktiskt tittar på.
    // Saknar projektet IA — eller finns inget projekt ännu — tar bokning@ över.
    // Vi faller medvetet INTE tillbaka på en IA från något av kundens andra
    // projekt: då hade fel person fått förfrågan.
    const projektetsIA = activeProject?.architect;
    const responsible = projektetsIA?.email
      ? projektetsIA
      : { firstName: 'Leadme-teamet', lastName: '', email: 'bokning@leadme.se' };

    switch (supplier.contactMethod || 'direct') {
      // Alternativ 1: Direktkontakt med leverantören via e-post
      case 'direct':
        if (!supplier.email) {
          // Saknar leverantören e-post går förfrågan till ansvarig i stället
          setSelectedSupplier(supplier);
          setContactResponsible(responsible);
          setShowLeadmeContactModal(true);
        } else {
          setSelectedSupplier(supplier);
          setShowSupplierEmailModal(true);
        }
        break;

      // Alternativ 2: Kontakt via Leadme — går till projektets IA, annars bokning@
      case 'leadme':
        setSelectedSupplier(supplier);
        setContactResponsible(responsible);
        setShowLeadmeContactModal(true);
        break;

      // Alternativ 3: Direktlänk till leverantörens sida (affiliate)
      case 'affiliate':
        if (supplier.affiliateUrl) {
          sendAffiliateClickNotification(supplier);
          window.open(supplier.affiliateUrl, '_blank', 'noopener,noreferrer');
        } else {
          setSelectedSupplier(supplier);
          setContactResponsible(null);
          setShowLeadmeContactModal(true);
        }
        break;

      default:
        setSelectedSupplier(supplier);
        setShowSupplierEmailModal(true);
    }
  }, [activeProject, sendAffiliateClickNotification]);

  const handleSaveOffer = useCallback(async (supplier) => {
    const arRedanFavorit = savedOffersSet.has(supplier._id);
    const nyaFavoriter = new Set(savedOffersSet);
    if (arRedanFavorit) nyaFavoriter.delete(supplier._id);
    else nyaFavoriter.add(supplier._id);
    setOptimistiskaFavoriter(nyaFavoriter);

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/suppliers/favorites/${supplier._id}`, {
        method: arRedanFavorit ? 'DELETE' : 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (!res.ok) throw new Error('Servern kunde inte spara favoriten');
      await refreshFavorites();
    } catch (error) {
      console.error('Fel vid sparande av favorit:', error);
    } finally {
      // Lämna över till serverns svar igen
      setOptimistiskaFavoriter(null);
    }
  }, [savedOffersSet, refreshFavorites]);

  const handleShowInfo = useCallback((supplier) => {
    setSelectedSupplier(supplier);
    setShowInfoModal(true);
  }, []);

  // Leverantörer som ska visas för kund, i prioriterad ordning
  const erbjudanden = useMemo(() => {
    return (suppliers || [])
      .filter(supplier => supplier.showInOffers !== false)
      .sort((a, b) => {
        const indexA = PRIORITERAD_ORDNING.indexOf(a.companyName);
        const indexB = PRIORITERAD_ORDNING.indexOf(b.companyName);
        if (indexA !== -1 && indexB !== -1) return indexA - indexB;
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;
        return (a.companyName || '').localeCompare(b.companyName || '');
      });
  }, [suppliers]);

  const modalProps = {
    selectedSupplier,
    showInfoModal,
    showLeadmeContactModal,
    showSupplierEmailModal,
    contactResponsible,
    projects,
    activeProject,
    procurement,
    onContact: handleContactRequest,
    onRequestSaved: fetchContactRequests,
    onCloseInfo: () => { setShowInfoModal(false); setSelectedSupplier(null); },
    onCloseLeadme: () => {
      setShowLeadmeContactModal(false);
      setContactResponsible(null);
      setSelectedSupplier(null);
    },
    onCloseSupplierEmail: () => { setShowSupplierEmailModal(false); setSelectedSupplier(null); },
  };

  return {
    erbjudanden,
    suppliersLoading,
    savedOffersSet,
    contactRequests,
    handleContactRequest,
    handleSaveOffer,
    handleShowInfo,
    modalProps,
  };
};

export default useErbjudanden;
